import { Button, Card, CardHeader, lighten, Stack } from '@mui/material';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Map from '/@/components/map';
import useUnitProvider from '/@/core/context/unit';
import { type Geometry, getBounds } from '/@/core/fields/geometry';
import { useMyFields } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { setGoBack } from '/@/store/nav/actions';
import { setPreferences } from '/@/store/session/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import { getMarkerIcon, isMultiPolygon, isPoint } from '/@/utils';
import ErrorDialog from './components/error-dialog';
import FieldNameModal from './components/name-modal';
import NewFieldOptionsSelector, { type NewFieldOption } from './components/options-selector';
import SuccessDialog from './components/success-dialog';
import DrawFieldV2 from './draw';
import { useCreateField } from './hooks/createField';
import ImportField from './import';
import UploadField from './upload';
import { convertCountryBoundsToGoogleLatLng } from './utils';

export type TNewField = {
    name?: '';
    geometry?: Geometry;
    altitude?: number;
    area?: number;
    source?: TEditOption | 'file-upload';
};

export type TEditOption = 'point' | 'polygon' | 'circle';

const NewFieldV2 = () => {
    const dispatch = useDispatch();

    const mapRef = useRef<Map | null>(null);
    const country = useSelector(getSessionCountry);
    const units = useUnitProvider();

    // Query Params
    const [searchParams] = useSearchParams();
    const userNodeId = searchParams.get('userNodeId');
    const queryOption = searchParams.get('option');
    const nextUrl = searchParams.get('next');

    const [newField, setNewField] = useState<TNewField>();
    const [selectedOption, setSelectedOption] = useState<NewFieldOption | undefined>(
        (queryOption as NewFieldOption) || undefined
    );
    const [editMode, setEditMode] = useState<TEditOption | undefined>();
    const [nameModalIsOpen, setNameModalIsOpen] = useState(false);
    const [error, setError] = useState();
    const [newFieldId, setNewFieldId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const zonesQuery = useZones({}); // Used inside createFieldhook (repeated to steal loading)

    const ownUserNodeQuery = useOwnUserNode();
    const allFieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,geometry,externalProvider',
        userNodeId: userNodeId ? Number(userNodeId) : undefined,
    });

    const onButtonClick = (selectedOption: NewFieldOption) => {
        setSelectedOption(selectedOption);
    };

    // Set Goback
    useEffect(() => {
        if (!selectedOption) dispatch(setGoBack(undefined));
        else dispatch(setGoBack(() => setSelectedOption(undefined)));
        return () => {
            dispatch(setGoBack(undefined));
        };
    }, [selectedOption]);

    // Center Map/Field
    useEffect(() => {
        if (!mapRef.current) return;
        if (newField?.geometry) {
            mapRef.current.fitBounds(getBounds(newField.geometry));
        } else if (allFieldsQuery.data?.results.length) {
            const bounds = new google.maps.LatLngBounds();
            allFieldsQuery.data.results.map(({ lat, lng }) => {
                bounds.extend(new google.maps.LatLng(lat, lng));
            });
            mapRef.current.fitBounds(bounds);
            setTimeout(
                ({ mapRef }) => {
                    if (!mapRef.current) return;
                    if (mapRef.current.getZoom() > 16) mapRef.current.setZoom(16);
                },
                100,
                { mapRef }
            );
        } else {
            const googleBounds = convertCountryBoundsToGoogleLatLng(country.bounds);
            mapRef.current.fitBounds(googleBounds);
        }
    }, [allFieldsQuery.data, country, newField?.geometry, selectedOption]);

    const newFieldForMap = useMemo(() => {
        if (newField?.geometry) {
            return [
                {
                    _id: 'NewField',
                    geometry: newField.geometry,
                    iconType: getMarkerIcon(newField),
                },
            ];
        }
        return [];
    }, [newField?.geometry]);

    const fieldIsValid = useMemo(() => {
        if (!newField?.geometry) return false;

        if (selectedOption === 'draw') {
            if (editMode === 'point') {
                return isPoint(newField.geometry);
            }
            if (editMode === 'polygon') {
                // Polygon must have at least 3 points in the outer ring
                if (isPoint(newField.geometry)) return false;
                const polygon = isMultiPolygon(newField.geometry) ? newField.geometry[0]! : newField.geometry;
                // Check outer ring (first element) has at least 3 coordinates
                return polygon.length > 0 && polygon[0]!.length >= 3;
            }
            if (editMode === 'circle') {
                return true;
            }
            return false;
        }

        // For other options, check if geometry exists and is valid
        if (isPoint(newField.geometry)) return true;
        const polygon = isMultiPolygon(newField.geometry) ? newField.geometry[0]! : newField.geometry;
        return polygon.length > 0 && polygon[0]!.length > 0;
    }, [newField, selectedOption, editMode]);

    const createField = useCreateField({
        setError,
        setNewField,
    });

    const allFields = useMemo(
        () =>
            (allFieldsQuery.data?.results || [])
                .concat(newFieldForMap)
                .map((f) => ({ ...f, iconType: getMarkerIcon(f) })),
        [allFieldsQuery.data, newFieldForMap]
    );

    const areaLabel = convertAreaFromM2(newField?.area || 0, units).toString();

    return (
        <Fragment>
            {selectedOption !== 'draw' && (
                <Map
                    ref={(ref) => {
                        mapRef.current = ref;
                    }}
                    fields={allFields}
                    clusterMarkers
                    noGeocoder
                />
            )}
            {selectedOption === undefined && (
                <NewFieldOptionsSelector
                    onButtonClick={onButtonClick}
                    showOptions={['draw', 'upload', 'import'].filter((option) => {
                        if (option === 'import') return ownUserNodeQuery.data?.id === Number(userNodeId);
                        return true;
                    })}
                    userNodeId={userNodeId ? Number(userNodeId) : undefined}
                />
            )}
            {selectedOption === 'upload' && !newField?.geometry && (
                <UploadField newField={newField} setNewField={setNewField} setSelectedOption={setSelectedOption} />
            )}
            {selectedOption === 'draw' && (
                <DrawFieldV2
                    newField={newField}
                    setNewField={setNewField}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    fields={allFieldsQuery.data?.results}
                />
            )}
            {selectedOption === 'import' && <ImportField />}
            {newFieldId && (
                <SuccessDialog
                    nextUrl={nextUrl}
                    fieldId={newFieldId}
                    onCreateAnotherFieldClick={() => {
                        setNewField(undefined);
                        setNewFieldId(null);
                        setNameModalIsOpen(false);
                        setEditMode(undefined);
                    }}
                />
            )}

            {/* MAP OVERLAYS */}
            {!!newField?.area && (
                <Card
                    sx={{
                        position: 'absolute',
                        bottom: '15%',
                        left: '50%',
                        transform: 'translate(-50%, 0)',
                        minWidth: (t) => t.spacing(12),
                    }}
                >
                    <CardHeader
                        title={`${areaLabel} ${_('aproximadas')}`}
                        titleTypographyProps={{ align: 'center' }}
                        sx={{ p: 1 }}
                    />
                </Card>
            )}
            {((selectedOption === 'draw' && editMode !== undefined) || newField?.geometry) && !newField?.name && (
                <Stack
                    direction={'row'}
                    spacing={2}
                    sx={{
                        position: 'absolute',
                        bottom: '5%',
                        left: '50%',
                        transform: 'translate(-50%, 0)',
                    }}
                >
                    <Button
                        color='secondary'
                        variant='contained'
                        sx={{ background: 'white', color: (t) => t.palette.secondary.main }}
                        onClick={() => {
                            setNewField(undefined);
                            setEditMode(undefined);
                        }}
                    >
                        {_('Limpiar')}
                    </Button>
                    <Button
                        color='secondary'
                        variant='contained'
                        onClick={() => {
                            setNameModalIsOpen(true);
                        }}
                        data-testid='add-field-button-save'
                        disabled={!fieldIsValid}
                        sx={{
                            '&.Mui-disabled': {
                                backgroundColor: lighten('#e8e8e8', 0.7),
                            },
                        }}
                    >
                        {_('Guardar')}
                    </Button>
                </Stack>
            )}
            {newField?.geometry && (
                <FieldNameModal
                    showName={nameModalIsOpen}
                    field={newField}
                    handleCancelName={() => {
                        setNewField((f) => ({ ...f, name: '' }));
                        setNameModalIsOpen(false);
                    }}
                    handleSubmit={async (name, area) => {
                        setNewField((f) => ({ ...f, name, area }));
                        setNameModalIsOpen(false);
                        setLoading(true);

                        try {
                            const fieldId = await createField({
                                newField: {
                                    ...newField,
                                    name,
                                    area,
                                    source: newField?.source || editMode,
                                    userNodeId: userNodeId ? Number(userNodeId) : undefined,
                                },
                            });

                            // Show success dialog
                            setNewFieldId(fieldId);
                            setLoading(false);
                        } catch (error) {
                            setError(error.message);
                            setNewFieldId(null);
                            setLoading(false);
                        }
                    }}
                    loading={zonesQuery.isValidating || loading}
                />
            )}

            <ErrorDialog
                type={error}
                visible={Boolean(error)}
                onAccept={async () => {
                    if (error === 'country' && newField) {
                        await dispatch(setPreferences({ country: newField.country }));
                        setError(undefined);
                        setNameModalIsOpen(true);
                        setLoading(false);
                        return;
                    }
                    setError(undefined);
                    setNewField(undefined);
                    setEditMode(undefined);
                    setLoading(false);
                }}
                onCancel={() => {
                    setError(undefined);
                    setNewField(undefined);
                    setEditMode(undefined);
                    setLoading(false);
                }}
                onClose={() => {
                    setError(undefined);
                    setNewField(undefined);
                    setEditMode(undefined);
                    setLoading(false);
                }}
            />
        </Fragment>
    );
};

export default NewFieldV2;
